import {IPromise} from "angular";
import {regNavn} from "felles/tjenester/tredjepartsTjenestenavn";
import {gettextCatalog} from "angular-gettext";
import {l} from "types/typescriptdefinisjoner_lederplan";
import {SakRessurs} from "felles/tjenester/ressurser/SakRessurs";
import {OppgaveRessurs} from "felles/tjenester/ressurser/OppgaveRessurs";
import {GruppeRessurs} from "felles/tjenester/ressurser/GruppeRessurs";
import {ModelloppdateringTjeneste} from "web/tjenester/ModelloppdateringTjeneste";
import {BeslutningRessurs} from "felles/tjenester/ressurser/BeslutningRessurs";
import {tilLokalDatoIgnorerTs, tilUTCSluttPåDagenUtenÅEndreDøgn} from "felles/tjenester/Datoer";
import {EnumOversettelser} from "felles/tjenester/i18n/EnumOversettelser";
import Oppgavestatus = l.Oppgavestatus;

export class OppgaveTjenesteWeb {

    static regNavn = "oppgaveTjenesteWeb";

    static forInjisering() {
        return [
            OppgaveRessurs.regNavn,
            EnumOversettelser.regNavn,
            SakRessurs.regNavn,
            BeslutningRessurs.regNavn,
            GruppeRessurs.regNavn,
            ModelloppdateringTjeneste.regNavn,
            regNavn.gettext.gettextCatalog,
            OppgaveTjenesteWeb
        ]
    }

    constructor(private oppgaveRes: OppgaveRessurs,
                private enumOversettelser: EnumOversettelser,
                private sakRes: SakRessurs,
                private beslutningRes: BeslutningRessurs,
                private gruppeRes: GruppeRessurs,
                private modelloppdatering: ModelloppdateringTjeneste,
                private gettextCatalog: gettextCatalog) {
    }

    initOppgaveSkjemaTilleggsdata(eiendeGruppeId: string): OppgavesskjemTilleggsdata {
        const tilleggsdata = {
            ansvarlige: [],
            gruppeId: eiendeGruppeId
        };
        this.gruppeRes.finnTilgjengeligeMedlemmer(eiendeGruppeId).$promise
          .then(medlemmer => tilleggsdata.ansvarlige = medlemmer);
        return tilleggsdata
    }

    initNySaksoppgaveSkjemaModell(sak: l.AgendaelementModell): Oppgavesskjemamodell {
        const modell = new Oppgavesskjemamodell(null);
        modell.forelderSak = sak;
        return modell;
    }

    initNyBeslutningsoppgaveSkjemaModell(beslutning: l.BeslutningModell): Oppgavesskjemamodell {
        const modell = new Oppgavesskjemamodell(null);
        modell.forelderBeslutning = beslutning;
        return modell;
    }

    initNyFrittståendeOppgaveSkjemaModell(): Oppgavesskjemamodell {
        return new Oppgavesskjemamodell(null);
    }

    initRedigerOppgaveSkjemaModell(oppgave: l.OppgaveModell): Oppgavesskjemamodell {
        return new Oppgavesskjemamodell(oppgave);
    }

    opprettOppgave(skjemamodell: Oppgavesskjemamodell): IPromise<l.OppgaveModell> {
        let nyOppgave = null;
        if (skjemamodell.forelderSak)
            nyOppgave = this.sakRes.opprettOppgave(skjemamodell.forelderSak.id, skjemamodell.somSpesifikasjon());

        if (skjemamodell.forelderBeslutning)
            nyOppgave = this.beslutningRes.opprettOppgave(skjemamodell.forelderBeslutning.id, skjemamodell.somSpesifikasjon());

        if (!skjemamodell.forelderSak && !skjemamodell.forelderBeslutning)
            nyOppgave = this.oppgaveRes.opprett(skjemamodell.somSpesifikasjon());

        return nyOppgave.$promise
          .then(nyOppgave => {
              this.modelloppdatering.broadcastLokalNyOppgave(nyOppgave, null);
              return nyOppgave;
          });
    }

    oppdaterOppgave(spesifikasjon: Oppgavesskjemamodell): IPromise<l.OppgaveModell> {
        return this.oppgaveRes.oppdater(spesifikasjon.somSpesifikasjon()).$promise
          .then(oppdatertOppgave => {
              this.modelloppdatering.broadcastLokalOppdatertOppgave(oppdatertOppgave, null);
              return oppdatertOppgave;
          })
    }

    /**
     * inverterUtførtStatus vil flippe oppgavens status.
     * Oppgavemodellen blir først modifisert når vi får svar fra API'et
     */
    inverterUtførtStatus(oppgave: l.OppgaveModell): IPromise<l.OppgaveModell> {
        let skalBliFullført = !oppgave.utfoert; // inverter status
        const spesifikasjon: l.OppgaveStatusSpesifikasjon = {
            id: oppgave.id,
            status: skalBliFullført ? l.Oppgavestatus.utført : l.Oppgavestatus.ikke_påbegynt,
            utfoertDato: skalBliFullført ? new Date() : null
        }
        return this.oppdaterStatus(spesifikasjon);
    }

    oppdaterStatus(spesifikasjon: l.OppgaveStatusSpesifikasjon): IPromise<l.OppgaveModell> {
        return this.oppgaveRes.settStatus(spesifikasjon).$promise
          .then(oppdatert => {
              this.modelloppdatering.broadcastLokalOppdatertOppgave(oppdatert, null);
              return oppdatert;
          });
    }

    slett(oppgave: l.OppgaveModell) {
        let slettPromis;
        if (!oppgave.forelderRef)
            slettPromis = this.oppgaveRes.slett(oppgave.id);
        else if (oppgave.forelderRef.type == "Sak")
            slettPromis = this.sakRes.slettOppgave(oppgave.forelderRef.id, oppgave.id);
        else if (oppgave.forelderRef.type == "Beslutning")
            slettPromis = this.beslutningRes.slettOppgave(oppgave.forelderRef.id, oppgave.id);
        else
            throw new Error(`Sletting av oppgavetype ${oppgave.forelderRef.type} er ikke implementert`);

        return slettPromis.$promise
          .then(() => this.modelloppdatering.broadcastLokalSlettetOppgave(oppgave.id, null));
    }

    muligeOppgavestatuser() {
        return [
            {navn: this.enumOversettelser.oversettOppgaveStatus(Oppgavestatus.ikke_påbegynt), status: l.Oppgavestatus.ikke_påbegynt},
            {navn: this.enumOversettelser.oversettOppgaveStatus(Oppgavestatus.påbegynt), status: l.Oppgavestatus.påbegynt},
            {navn: this.enumOversettelser.oversettOppgaveStatus(Oppgavestatus.utført), status: l.Oppgavestatus.utført}
        ];
    }
}

export class Oppgavesskjemamodell {

    constructor(mal: l.OppgaveModell) {
        this.spesifikasjon = {
            id: mal ? mal.id : null,
            tittel: mal ? mal.tittel : null,
            ansvarligId: mal && mal.ansvarlig ? mal.ansvarlig.id : null,
            frist: mal ? tilLokalDatoIgnorerTs(mal.frist) : null,
            beskrivelse: mal ? mal.beskrivelse : null,
            status: mal ? mal.status : null,
            utfoertDato: mal?.utfoertDato ? new Date(mal.utfoertDato) : null,
        };
        this.ansvarlig = mal ? mal.ansvarlig : null;
    }

    spesifikasjon: l.MuterendeOppgaveSpesifikasjon;
    ansvarlig: l.GruppeMedlemListeModell;
    forelderSak: l.AgendaelementModell; //satt ved opprettelse av saksoppgave
    forelderBeslutning: l.BeslutningModell; //satt ved opprettelse av beslutningsoppgave

    somSpesifikasjon(): l.MuterendeOppgaveSpesifikasjon {
        this.spesifikasjon.frist = tilUTCSluttPåDagenUtenÅEndreDøgn(this.spesifikasjon.frist);

        this.spesifikasjon.ansvarligId = this.ansvarlig.id;
        return this.spesifikasjon;
    }
}

export interface OppgavesskjemTilleggsdata {
    ansvarlige: l.GruppeMedlemListeModell[];
    gruppeId: string;
}

export interface OppgaveStatusOgNavn {
    status: l.Oppgavestatus;
    navn: string;
}

